import { useCallback } from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import {
    faArchive,
    faArrowDown,
    faArrowUp,
    faCircle,
    faEllipsisV,
    faTrash,
} from '@fortawesome/pro-regular-svg-icons';
import { faChevronRight, faCircleCheck } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fetchTaskById, fetchTaskIds } from 'api/tasks';
import * as Drawer from 'components/drawer';
import * as DropdownMenu from 'components/dropdown-menu';
import { useFiltersByKey } from 'components/filter/filters-next/useFiltersByKey';
import * as Form from 'components/form';
import { StarTaskAction } from 'components/tasks/StarTaskAction';
import { taskFiltersToRequest } from 'components/tasks/form/upstream';
import { CopyTaskLinkButton } from 'components/tasks/single/CopyTaskLinkButton';
import { DeleteTaskAlertDialog } from 'components/tasks/single/DeleteTaskAlertDialog';
import { useTaskDrawerState } from 'components/tasks/useTaskDrawerState';
import { injectUnarchivedFilter } from 'components/tasks/utils/upstream';
import { TaskFormValues } from 'components/tasks/utils/useTaskForm';
import {
    taskFilterValuesSelector,
    useTasksFilterStore,
} from 'components/tasks/utils/useTasksFilterStore';
import { useUpdateTask } from 'components/tasks/utils/useUpdateTask';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { accountQueryKeys } from 'queries/account-query-keys';
import { TaskStatus } from 'types/domain';

const TaskHeader = ({ taskId }: { taskId: number }) => {
    const { accountId } = useCurrentAccount();
    const { setSelectedTaskId } = useTaskDrawerState();
    const { reset, getValues } = Form.useGivebutterFormContext<TaskFormValues>();

    const { data: task } = useSuspenseQuery({
        queryKey: accountQueryKeys.taskById(accountId, taskId),
        queryFn: () => fetchTaskById(accountId, taskId),
    });

    const taskFilterValues = useTasksFilterStore(taskFilterValuesSelector);
    const { filters, filterGroups } = useFiltersByKey('tasks');
    const hasArchivedFilter = filterGroups.find(f =>
        f.filters.find(f => f.serverKey === 'archived'),
    );
    const requestFilters = hasArchivedFilter ? filters : injectUnarchivedFilter(filters);

    const storeFilterParams = taskFiltersToRequest(taskFilterValues);
    const taskIdParams = {
        ...storeFilterParams,
        filters: requestFilters,
    };
    const { data: taskIds } = useSuspenseQuery({
        queryKey: accountQueryKeys.taskIdsWithFilters(accountId, taskIdParams),
        queryFn: () => fetchTaskIds(accountId, taskIdParams),
    });

    const { mutate: updateTask } = useUpdateTask();

    const taskIndex = taskIds.indexOf(task.id);

    const handleStatusUpdate = useCallback(
        (status: TaskStatus) => {
            updateTask(
                { taskId: task.id, params: { status: status } },
                {
                    onSuccess: () =>
                        reset({
                            ...getValues(),
                            status: status,
                        }),
                },
            );
        },
        [reset, getValues, updateTask, task.id],
    );

    return (
        <div className="flex flex-col">
            <div className="px-8 pb-4 pt-6">
                <div className="flex w-full flex-col gap-4 border-b border-gray-200 pb-4">
                    <div className="flex items-center justify-between gap-10">
                        <div className="flex items-center gap-2 overflow-hidden">
                            <Button
                                variant="none"
                                size="sm"
                                className="font-medium text-gray-600"
                                onClick={() => setSelectedTaskId(null)}
                            >
                                Tasks
                            </Button>
                            <FontAwesomeIcon
                                size="xs"
                                className="text-gray-300"
                                icon={faChevronRight}
                            />
                            <Drawer.Title className="truncate text-sm font-medium text-gray-600">
                                {task.title}
                            </Drawer.Title>
                        </div>
                        <StarTaskAction task={task} />
                    </div>

                    <div className="flex flex-col items-start gap-4 sm:flex-row sm:justify-between">
                        {task.completed_at ? (
                            <Button
                                variant="outline"
                                color="neutral"
                                size="sm"
                                onClick={() => handleStatusUpdate('todo')}
                            >
                                Completed{' '}
                                <FontAwesomeIcon icon={faCircleCheck} className="text-green-500" />
                            </Button>
                        ) : (
                            <Button
                                variant="outline"
                                color="neutral"
                                size="sm"
                                onClick={() => handleStatusUpdate('complete')}
                            >
                                Mark complete{' '}
                                <FontAwesomeIcon icon={faCircle} className="text-gray-300" />
                            </Button>
                        )}

                        <div className="flex items-center gap-1">
                            <span className="mr-1 text-sm text-gray-900">
                                {taskIndex + 1}{' '}
                                <span className="text-gray-500">/ {taskIds.length}</span>
                            </span>
                            <Button
                                variant="outline"
                                color="neutral"
                                shape={'square'}
                                size="sm"
                                className="!w-8"
                                onClick={() => {
                                    const newIndex =
                                        taskIndex === 0 ? taskIds.length - 1 : taskIndex - 1;
                                    setSelectedTaskId(taskIds[newIndex]);
                                }}
                                aria-label="Previous task"
                            >
                                <FontAwesomeIcon icon={faArrowUp} />
                            </Button>
                            <Button
                                variant="outline"
                                color="neutral"
                                shape={'square'}
                                size="sm"
                                className="!w-8"
                                onClick={() => {
                                    const nextIndex = taskIndex + 1;
                                    const newIndex = nextIndex < taskIds.length ? nextIndex : 0;
                                    setSelectedTaskId(taskIds[newIndex]);
                                }}
                                aria-label="Next task"
                            >
                                <FontAwesomeIcon icon={faArrowDown} />
                            </Button>
                            <CopyTaskLinkButton />
                            <DropdownMenu.Root>
                                <DropdownMenu.Trigger asChild>
                                    <Button
                                        variant="outline"
                                        color="neutral"
                                        shape={'square'}
                                        size="sm"
                                        className="!w-8"
                                        aria-label="Menu"
                                    >
                                        <FontAwesomeIcon icon={faEllipsisV} />
                                    </Button>
                                </DropdownMenu.Trigger>
                                <DropdownMenu.Content align="end" sideOffset={8}>
                                    <DropdownMenu.Item
                                        className="flex gap-2"
                                        onSelect={() => {
                                            updateTask({
                                                taskId: task.id,
                                                params: { archived: !task.archived_at },
                                            });
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faArchive} />
                                        {task.archived_at ? 'Unarchive task' : 'Archive task'}
                                    </DropdownMenu.Item>
                                    <DeleteTaskAlertDialog
                                        taskId={task.id}
                                        onSuccess={() => setSelectedTaskId(null)}
                                    >
                                        <DropdownMenu.Item
                                            onSelect={e => e.preventDefault()}
                                            className="flex gap-2 text-red-600 focus-visible:before:border-0"
                                        >
                                            <FontAwesomeIcon icon={faTrash} />
                                            Delete task
                                        </DropdownMenu.Item>
                                    </DeleteTaskAlertDialog>
                                </DropdownMenu.Content>
                            </DropdownMenu.Root>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export { TaskHeader };
